import React, { useMemo } from 'react';
import {
  EmployeeObjectiveCategoryAssignmentFragment,
  ObjectiveCategoryFragment,
  ObjectiveTimePeriodFragment,
  useEmployeeAssignedCategoriesQuery,
  useGoalsContextPeriodsQuery,
} from '../../../../__generated__/graphql';
import { useFlairLocalStorage } from '../../../../hooks/useFlairLocalStorage';
import { useUserInfo } from '../../../../context/UserInfo';
import ServerError from '../../../../../../components/ServerError';

export type GoalsContextType = {
  isManagerView: boolean;
  allActivePeriods: readonly ObjectiveTimePeriodFragment[];
  currentPeriod: ObjectiveTimePeriodFragment | null;
  currentPeriodId: string | null;
  setCurrentPeriodId: (periodId: string | null) => void;
  loading: boolean;
  developmentFrameworkEnabled: boolean;
  currentPeriodGoalCategories: readonly ObjectiveCategoryFragment[];
  employeeAssignedCategories: readonly EmployeeObjectiveCategoryAssignmentFragment[];
};

const GoalsContext = React.createContext<GoalsContextType>({
  isManagerView: false,
  allActivePeriods: [],
  currentPeriod: null,
  currentPeriodId: null,
  setCurrentPeriodId: () => {},
  loading: true,
  developmentFrameworkEnabled: false,
  currentPeriodGoalCategories: [],
  employeeAssignedCategories: [],
});

type Props = {
  isManagerView: boolean;
};

export const GoalsContextProvider: React.FC<Props> = (props) => {
  const [currentPeriodIdFromStorage, setCurrentPeriodIdFromStorage] =
    useFlairLocalStorage('goals_current_period_id', null);
  const { data, loading, error } = useGoalsContextPeriodsQuery();
  const { id: employeeId } = useUserInfo();

  const allActivePeriods = useMemo(
    () => data?.activeObjectivePeriods ?? [],
    [data?.activeObjectivePeriods],
  );

  const currentPeriod = useMemo(() => {
    if (!currentPeriodIdFromStorage) {
      return null;
    }
    return (
      allActivePeriods.find(
        (period) => period.Id === currentPeriodIdFromStorage,
      ) ?? null
    );
  }, [currentPeriodIdFromStorage, allActivePeriods]);

  const currentPeriodId = useMemo(() => {
    return currentPeriod?.Id ?? null;
  }, [currentPeriod]);

  const frameworkId = currentPeriod?.framework?.Id ?? null;

  const developmentFrameworkEnabled = useMemo(
    () => allActivePeriods.length > 0,
    [allActivePeriods.length],
  );

  const currentPeriodGoalCategories = useMemo(
    () => currentPeriod?.framework?.categories ?? [],
    [currentPeriod?.framework?.categories],
  );

  const { data: assignedCategoriesData } = useEmployeeAssignedCategoriesQuery({
    variables: {
      frameworkId: frameworkId!,
      employeeId,
      periodId: currentPeriodId!,
    },
    skip: !frameworkId || !currentPeriodId || !employeeId,
  });

  const employeeAssignedCategories = useMemo(
    () =>
      assignedCategoriesData?.developmentFramework
        ?.employeeAssignedCategories ?? [],
    [assignedCategoriesData?.developmentFramework?.employeeAssignedCategories],
  );

  // we don't need to useEffect for this (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes)
  if (currentPeriodIdFromStorage === null && allActivePeriods.length === 1) {
    setCurrentPeriodIdFromStorage(allActivePeriods[0].Id);
  }
  if (error) {
    return <ServerError />;
  }

  return (
    <GoalsContext.Provider
      value={{
        isManagerView: props.isManagerView,
        allActivePeriods,
        currentPeriod,
        currentPeriodId,
        setCurrentPeriodId: setCurrentPeriodIdFromStorage,
        loading,
        developmentFrameworkEnabled,
        currentPeriodGoalCategories,
        employeeAssignedCategories,
      }}
      {...props}
    />
  );
};

export const useGoalsContext = (): GoalsContextType =>
  React.useContext(GoalsContext);
